import {
  UserListResponse,
  User,
  UserCreate,
  UserCreateEncrypted,
  UserUpdate,
  Role,
} from "../types/users";

import apiClient from "./api";

class UsersService {
  /**
   * Get all users with filtering options
   */
  async getUsers(
    params: {
      skip?: number;
      limit?: number;
      search?: string;
      role_id?: number;
      is_active?: boolean;
    } = {},
  ): Promise<UserListResponse> {
    return await apiClient.get<UserListResponse>("/users", { params });
  }

  /**
   * Get user by ID
   */
  async getUserById(id: number): Promise<User> {
    return await apiClient.get<User>(`/users/${id}`);
  }

  /**
   * Create a new user
   */
  async createUser(userData: UserCreate): Promise<User> {
    return await apiClient.post<User>("/users", userData);
  }

  /**
   * Create a new user with encrypted password
   */
  async createUserEncrypted(userData: UserCreateEncrypted): Promise<User> {
    return await apiClient.post<User>("/users/encrypted", userData);
  }

  /**
   * Update an existing user
   */
  async updateUser(id: number, userData: UserUpdate): Promise<User> {
    return await apiClient.put<User>(`/users/${id}`, userData);
  }

  /**
   * Update user password
   */
  async updateUserPassword(id: number, password: string): Promise<void> {
    return await apiClient.put<void>(
      `/users/${id}/password?password=${encodeURIComponent(password)}`,
    );
  }

  /**
   * Update user status (active/inactive)
   */
  async updateUserStatus(id: number, isActive: boolean): Promise<void> {
    return await apiClient.put<void>(
      `/users/${id}/status?is_active=${isActive}`,
    );
  }

  /**
   * Get all available user roles
   */
  async getRoles(): Promise<Role[]> {
    return await apiClient.get<Role[]>("/users/roles");
  }

  /**
   * Create a new role
   */
  async createRole(roleData: {
    name: string;
    description?: string;
  }): Promise<Role> {
    return await apiClient.post<Role>("/users/roles", roleData);
  }
}

// Create singleton instance
export const usersService = new UsersService();
